import {BaseRowData} from '../../../types/index.js'
import {chakra} from '@chakra-ui/react'
import {CatchBoundary} from '@tanstack/react-router'
import {flexRender, Row} from '@tanstack/react-table'
import {ComponentProps, memo} from 'react'

interface InfiniteTableRowProps<RowData extends BaseRowData> extends ComponentProps<typeof chakra.div> {
  /**
   * Because of the virtualization, the `row` can sometimes be undefined, so be careful when using it.
   */
  row: Row<RowData> | undefined
  /**
   * We need to pass it explicitly to be able to rerender the row.
   */
  selected: boolean
  height?: number
}

export const InfiniteTableRow = memo(
  function InfiniteTableRowComponent<RowData extends BaseRowData>({
    row,
    height = 48,
    ...props
  }: InfiniteTableRowProps<RowData>) {
    return (
      <chakra.div
        position="absolute"
        top="0"
        left="0"
        height={`${height}px`}
        width="100%"
        display="flex"
        alignItems="center"
        borderBottomWidth="1px"
        borderColor="border"
        willChange="transform"
        transformOrigin="top left"
        contain="layout paint size style"
        backfaceVisibility="hidden"
        data-e2e={row?.id}
        data-role="table-row"
        _hover={{bg: 'bg.muted'}}
        _focus={{bg: 'bg.subtle'}}
        {...props}
      >
        <CatchBoundary getResetKey={() => 'reset'} onCatch={console.error}>
          {row?.getVisibleCells().map(cell => {
            const meta = cell.column.columnDef.meta as Record<string, (row: unknown) => Record<string, unknown> | undefined> | undefined
            const props = meta?.getCellContainerProps?.(cell.row.original)

            return (
              <chakra.div
                key={cell.id}
                position="relative"
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                display="flex"
                alignItems="center"
                fontSize="sm"
                padding="3"
                flex="1 1 0%"
                width="100%"
                maxWidth={`${cell.column.getSize()}%`}
                minWidth={cell.column.columnDef.minSize ? `${cell.column.columnDef.minSize}px` : undefined}
                data-e2e={cell.id}
                {...props}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </chakra.div>
            )
          })}
        </CatchBoundary>

        {props.children}
      </chakra.div>
    )
  },
  () => {
    return false

    // // false -> props are not equal, render
    // // true -> props are equal, skip render

    // const prevRow = prevProps.row
    // const nextRow = nextProps.row

    // // different row -> re-render
    // if (prevRow?.original?.id !== nextRow?.original?.id) return false
    // if (prevProps.selected !== nextProps.selected) return false
    // if (prevRow?.original && 'status' in prevRow.original && nextRow?.original && 'status' in nextRow.original) {
    //   return prevRow.original.status === nextRow.original.status
    // }

    // // nothing changed -> skip render
    // return true
  }
)
